import { redactHuddleInvitationUrl } from '@naumu/shared/huddles/redact-url';
/**
 * First-touch / last-touch marketing attribution — pure logic.
 *
 * Every landing is turned into a `TouchSnapshot` (click ids, UTMs, referrer,
 * landing path, timestamp). Two snapshots are kept per visitor:
 *
 *   - **first** — the landing that introduced them to Naumu. Written once and
 *     NEVER overwritten for the life of the record. This is the one that tells
 *     you which campaign actually created the account.
 *   - **last**  — the most recent landing that carried a real attribution
 *     signal. Overwritten every time one arrives; landings with no signal
 *     (internal navigation, direct return visits) leave it alone, otherwise a
 *     user re-opening the app would erase the campaign that brought them back.
 *
 * The record expires 90 days after its last write (rolling, matching both the
 * cookie's max-age and Google's click-attribution window). An expired record is
 * discarded on read, so the next landing legitimately becomes a NEW first touch.
 *
 * Persistence lives in ./touch-storage.ts; this module is side-effect free and
 * fully unit-testable.
 */

import { REFERRAL_CODE_SHAPE } from '@naumu/shared/referrals/referrals-types';

/** 90 days in ms — cookie max-age, localStorage TTL, and Ads click window. */
export const TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Hard cap on any single persisted value. Mirrors the backend's own validation,
 * so a hostile URL can't inflate a User node (or the cookie) with megabytes of
 * junk.
 *
 * NOTE: this cap alone does NOT keep the cookie under the browser's 4096-byte
 * per-cookie limit — two fully-populated snapshots serialize to ~5.3KB. The
 * size guard lives in `writeCookie` (./touch-storage.ts), which drops the
 * cookie copy and leaves localStorage as the record's home when it overflows.
 */
export const MAX_TOUCH_VALUE_LENGTH = 255;

/** Schema version, so a future shape change can invalidate old records. */
export const TOUCH_RECORD_VERSION = 1;

/** One landing, normalized. Every field except `at` is optional. */
export interface TouchSnapshot {
	gclid?: string;
	fbclid?: string;
	utmSource?: string;
	utmMedium?: string;
	utmCampaign?: string;
	utmTerm?: string;
	utmContent?: string;
	/** External referrer only — same-origin referrers are dropped. */
	referrer?: string;
	/** Path (+ hash-free) of the landing page, query string stripped. */
	landingPath?: string;
	/**
	 * Referral code (`User.referralCode`, spec POST_ONBOARDING_OBJECTIVES.md
	 * section 6). Read off `/r/<code>` landings and `?ref=` params; replayed to
	 * the backend at signup, which stamps `User.referredByUserId` from it.
	 */
	ref?: string;
	/** ISO-8601 timestamp of the landing. */
	at: string;
}

export interface TouchAttribution {
	version: number;
	first: TouchSnapshot;
	last: TouchSnapshot;
	/** ms epoch after which the whole record is stale and must be dropped. */
	expiresAt: number;
}

/** The query params we read off a landing URL, mapped to snapshot fields. */
const PARAM_TO_FIELD: ReadonlyArray<[string, keyof TouchSnapshot]> = [
	['gclid', 'gclid'],
	['fbclid', 'fbclid'],
	['utm_source', 'utmSource'],
	['utm_medium', 'utmMedium'],
	['utm_campaign', 'utmCampaign'],
	['utm_term', 'utmTerm'],
	['utm_content', 'utmContent'],
	['ref', 'ref'],
];

/**
 * `/r/<code>` - the personal referral link's path shape. The marketing route
 * of the same name validates the code and bounces to `/`; capturing it here
 * means the touch record carries the code before that redirect happens.
 */
const REFERRAL_PATH = /^\/r\/([^/]+)\/?$/;

/** The referral code carried by a landing path, or undefined. */
export const readReferralCodeFromPath = (pathname: string): string | undefined =>
	sanitizeReferralCode(REFERRAL_PATH.exec(pathname)?.[1]);

/**
 * A `ref` becomes a touch signal (and overwrites last touch) only when it is
 * shaped like a real code: the shared shape keeps a 255-character `?ref=`
 * from posing as a referral. Lowercased so the backend lookup is exact.
 */
export const sanitizeReferralCode = (value: unknown): string | undefined => {
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	return REFERRAL_CODE_SHAPE.test(trimmed) ? trimmed.toLowerCase() : undefined;
};

/** Fields that on their own make a landing "attributed". */
const SIGNAL_FIELDS: ReadonlyArray<keyof TouchSnapshot> = [
	'gclid',
	'fbclid',
	'utmSource',
	'utmMedium',
	'utmCampaign',
	'utmTerm',
	'utmContent',
	'ref',
];

/** Trim, drop empties, and truncate to the shared max length. */
export const sanitizeTouchValue = (value: unknown): string | undefined => {
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	if (!trimmed) return undefined;
	return trimmed.slice(0, MAX_TOUCH_VALUE_LENGTH);
};

/**
 * Build a snapshot from a landing URL + referrer.
 *
 * Both inputs are strings so this stays testable without a DOM. A referrer on
 * the same origin as the landing URL is dropped — it is internal navigation,
 * not a new source.
 */
export const buildTouchSnapshot = (input: {
	url: string;
	referrer?: string;
	now?: Date;
}): TouchSnapshot => {
	const now = input.now ?? new Date();
	const snapshot: TouchSnapshot = { at: now.toISOString() };

	let parsed: URL | null = null;
	try {
		parsed = new URL(input.url);
	} catch {
		parsed = null;
	}

	if (parsed) {
		for (const [param, field] of PARAM_TO_FIELD) {
			const raw = parsed.searchParams.get(param);
			const value = field === 'ref' ? sanitizeReferralCode(raw) : sanitizeTouchValue(raw);
			if (value) snapshot[field] = value;
		}
		// Query string deliberately stripped: the params are already captured as
		// their own fields, and keeping them would blow the length cap on long
		// campaign URLs.
		const path = sanitizeTouchValue(redactHuddleInvitationUrl(parsed.pathname));
		if (path) snapshot.landingPath = path;
		const pathRef = readReferralCodeFromPath(parsed.pathname);
		if (pathRef && !snapshot.ref) snapshot.ref = pathRef;
	}

	const referrer = sanitizeTouchValue(input.referrer);
	if (referrer && isAttributableReferrer(referrer, parsed)) {
		snapshot.referrer = redactHuddleInvitationUrl(referrer);
	}

	return snapshot;
};

/**
 * Identity providers we bounce THROUGH during sign-in.
 *
 * Without this list the OAuth round trip would land back on Naumu with
 * `document.referrer === 'https://accounts.google.com/…'`, which looks like a
 * brand-new external source and would overwrite the real last touch (the gclid
 * landing) at the exact moment the signup conversion is recorded. It is the
 * single most damaging false positive in the whole module.
 */
const IDP_REFERRER_HOSTS: readonly string[] = [
	'accounts.google.com',
	'accounts.youtube.com',
	'login.microsoftonline.com',
	'login.live.com',
	'login.microsoft.com',
	'appleid.apple.com',
];

/** Naive registrable domain: the last two labels. Enough for our own hosts. */
const registrableDomain = (host: string): string => host.split('.').slice(-2).join('.');

/** `true` when a referrer represents a genuinely new external source. */
const isAttributableReferrer = (referrer: string, landing: URL | null): boolean => {
	let host: string;
	try {
		host = new URL(referrer).host.toLowerCase();
	} catch {
		// Unparseable referrer — keep it rather than silently dropping a real
		// source; the backend caps its length either way.
		return true;
	}
	if (IDP_REFERRER_HOSTS.includes(host)) return false;
	if (!landing) return true;
	// Same site, not just same host: `naumu.ai` → `app.naumu.ai` is internal
	// navigation, not a new source.
	return registrableDomain(host) !== registrableDomain(landing.host.toLowerCase());
};

/** `true` when the landing carries a click id, a UTM, or an external referrer. */
export const hasTouchSignal = (snapshot: TouchSnapshot): boolean => {
	if (SIGNAL_FIELDS.some((field) => Boolean(snapshot[field]))) return true;
	return Boolean(snapshot.referrer);
};

/** `true` when the record is missing or past its TTL. */
export const isTouchRecordExpired = (
	record: TouchAttribution | null,
	now: number,
): boolean => {
	if (!record) return true;
	if (record.version !== TOUCH_RECORD_VERSION) return true;
	return record.expiresAt <= now;
};

/**
 * Fold a landing into the stored record.
 *
 * Returns the record to persist, or `null` when nothing needs writing (an
 * unattributed landing on top of a live record — the common case for every
 * in-app page view, so this keeps the cookie write off the hot path).
 *
 * Rules:
 *   - No live record → the landing becomes BOTH first and last touch,
 *     regardless of whether it carries a signal. A direct visitor still has a
 *     first touch (`referrer` empty, `landingPath` set); that IS the answer.
 *   - Live record + signal → `last` replaced, `first` untouched, TTL renewed.
 *   - Live record, no signal → no write.
 */
export const mergeTouchAttribution = (
	existing: TouchAttribution | null,
	incoming: TouchSnapshot,
	now: number,
): TouchAttribution | null => {
	if (isTouchRecordExpired(existing, now)) {
		return {
			version: TOUCH_RECORD_VERSION,
			first: incoming,
			last: incoming,
			expiresAt: now + TOUCH_TTL_MS,
		};
	}

	// `existing` is non-null here — isTouchRecordExpired returns true for null.
	const live = existing as TouchAttribution;
	if (!hasTouchSignal(incoming)) return null;

	return {
		version: TOUCH_RECORD_VERSION,
		// First touch is immutable for the life of the record. This is the whole
		// point of the module — do not "improve" it by merging in newer values.
		first: live.first,
		last: incoming,
		expiresAt: now + TOUCH_TTL_MS,
	};
};

/** Parse an untrusted stored value back into a record, or null if unusable. */
export const parseTouchRecord = (raw: unknown): TouchAttribution | null => {
	if (typeof raw !== 'string' || !raw) return null;
	let decoded: unknown;
	try {
		decoded = JSON.parse(raw);
	} catch {
		return null;
	}
	if (!decoded || typeof decoded !== 'object') return null;
	const candidate = decoded as Partial<TouchAttribution>;
	if (candidate.version !== TOUCH_RECORD_VERSION) return null;
	if (typeof candidate.expiresAt !== 'number') return null;
	const first = parseSnapshot(candidate.first);
	const last = parseSnapshot(candidate.last);
	if (!first || !last) return null;
	return { version: TOUCH_RECORD_VERSION, first, last, expiresAt: candidate.expiresAt };
};

const parseSnapshot = (raw: unknown): TouchSnapshot | null => {
	if (!raw || typeof raw !== 'object') return null;
	const source = raw as Record<string, unknown>;
	const at = sanitizeTouchValue(source.at);
	if (!at) return null;
	const snapshot: TouchSnapshot = { at };
	for (const field of [...SIGNAL_FIELDS, 'referrer', 'landingPath'] as const) {
		const value = sanitizeTouchValue(source[field]);
		if (value) snapshot[field] = value;
	}
	return snapshot;
};
